import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactModal from "react-modal";
import dayjs from "dayjs";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Slider } from "@mui/material";
import { ChevronDown, X, Calendar as CalendarIcon } from "lucide-react";

import { TranslatedText } from "@/components/common/text";
import { CalendarPanel, MultiSelectField } from "@/components/common/inputs";
import { LocationDropdown } from "@/components/search";
import { radiusTypes } from "@/utils/constants";
import { useDebounce } from "@/utils/useDebounce";
import { fetchRequirementsLoads } from "@/services/loadFinderApi";
import { API } from "@/services/API";

const earnMoreOnLabels = ["Line", "Line-Balanced", "Balanced", "Exit-Balanced", "Exit"];
const lineExitMarks = [0, 1, 2, 3, 4].map((m) => ({ value: m }));
// Backend StopsFilter enum: any | single (= 2 stops, pickup + delivery) | multi (>2 stops)
const stopsOptions = [
    { id: "any", text: "Any" },
    { id: "single", text: "Single (2 stops)" },
    { id: "multi", text: "Multi (3+ stops)" },
];

const COUNT_DEBOUNCE_MS = 600;
const WEIGHT_MIN = 0;
const WEIGHT_MAX = 80000;
const WEIGHT_STEP = 1000;
const OUTBOUND_MIN = 0;
const OUTBOUND_MAX = 3000;
const OUTBOUND_STEP = 50;

const FieldLabel = ({ children }) => (
    <span className="block text-[12px] font-medium uppercase tracking-wider text-cm-p-text-secondary mb-1">
        <TranslatedText>{children}</TranslatedText>
    </span>
);

const SelectField = ({ value, onChange, options, placeholder = "Select" }) => (
    <Listbox value={value?.id ?? null} onChange={(id) => onChange(options.find((o) => o.id === id) ?? null)}>
        {({ open }) => (
            <div className="relative">
                <ListboxButton
                    className={`w-full flex items-center justify-between bg-cm-p-surface-container rounded-[6px] px-3 h-[42px] text-cm-p-text-primary text-[14px] font-medium border ${
                        open ? "border-cm-p-accent-200" : "border-cm-p-outline"
                    } focus:outline-none`}
                >
                    <span className={value ? "text-cm-p-text-primary" : "text-cm-p-text-secondary"}>{value?.text ?? placeholder}</span>
                    <ChevronDown className={`w-4 h-4 text-cm-p-text-secondary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </ListboxButton>
                <ListboxOptions
                    anchor={{ to: "bottom start", gap: 4 }}
                    className="z-[1100] w-[var(--button-width)] bg-cm-p-surface rounded-lg border border-cm-p-outline shadow-lg py-1"
                >
                    {options.map((option) => (
                        <ListboxOption
                            key={option.id}
                            value={option.id}
                            className="px-4 py-2 cursor-pointer hover:bg-cm-p-surface-hover text-cm-p-text-primary text-[14px] font-medium data-[focus]:bg-cm-p-surface-hover"
                        >
                            {option.text}
                        </ListboxOption>
                    ))}
                </ListboxOptions>
            </div>
        )}
    </Listbox>
);

const TextField = ({ value, onChange, placeholder = "Any", numeric = false, prefix }) => {
    const handleChange = (e) => {
        const next = e.target.value;
        if (numeric && next !== "" && !/^\d*\.?\d*$/.test(next)) return;
        onChange(next);
    };
    return (
        <div className="relative">
            {prefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cm-p-text-primary text-[14px] font-medium pointer-events-none">
                    {prefix}
                </span>
            )}
            <input
                type="text"
                inputMode={numeric ? "decimal" : "text"}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                className={`w-full h-[42px] bg-cm-p-surface-container border border-cm-p-outline rounded-[6px] text-[14px] font-medium text-cm-p-text-primary placeholder:text-cm-p-text-secondary focus:outline-none focus:border-cm-p-accent-200 ${
                    prefix ? "pl-7 pr-3" : "px-3"
                }`}
            />
        </div>
    );
};

const DateRangeField = ({ startDate, endDate, onChangeStart, onChangeEnd, onClear, minDate, placeholder = "Select dates" }) => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const [coords, setCoords] = useState(null);

    useLayoutEffect(() => {
        if (!open || !buttonRef.current) return;
        const update = () => {
            const r = buttonRef.current?.getBoundingClientRect();
            if (r) setCoords({ top: r.bottom + 4, left: r.left });
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open]);

    const display = (() => {
        if (startDate && endDate && startDate !== endDate) return `${dayjs(startDate).format("MMM D")} - ${dayjs(endDate).format("MMM D")}`;
        if (startDate) return dayjs(startDate).format("MMM D");
        return placeholder;
    })();

    // The first click is committed straight away as a single day, so closing the popover keeps
    // it; a second click widens the selection into a range and closes.
    const handleChange = (val) => {
        if (!Array.isArray(val)) return;
        const [start, end] = val;
        const startStr = dayjs(start).format("YYYY-MM-DD");
        onChangeStart(startStr);
        onChangeEnd(end ? dayjs(end).format("YYYY-MM-DD") : startStr);
        if (end) setOpen(false);
    };

    const setToday = () => {
        const todayStr = dayjs().format("YYYY-MM-DD");
        onChangeStart(todayStr);
        onChangeEnd(todayStr);
        setOpen(false);
    };

    const clear = onClear
        ? () => {
              onClear();
              setOpen(false);
          }
        : undefined;

    const value =
        startDate && endDate
            ? [dayjs(startDate).toDate(), dayjs(endDate).toDate()]
            : startDate
              ? dayjs(startDate).toDate()
              : undefined;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`w-full flex items-center justify-between bg-cm-p-surface-container rounded-[6px] px-3 h-[42px] gap-2 border ${
                    open ? "border-cm-p-accent-200" : "border-cm-p-outline"
                }`}
            >
                <span className={`text-[14px] font-medium ${startDate ? "text-cm-p-text-primary" : "text-cm-p-text-secondary"}`}>
                    {display}
                </span>
                <CalendarIcon className="w-4 h-4 text-cm-p-text-secondary shrink-0" />
            </button>
            {open &&
                coords &&
                createPortal(
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <div
                            style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 1100 }}
                            className="bg-cm-p-surface w-[307px] rounded-xl drop-shadow-[0_2px_4px_rgba(33,33,33,0.12)] border border-cm-p-outline"
                        >
                            <CalendarPanel
                                onChange={handleChange}
                                value={value}
                                selectRange
                                minDate={minDate ?? new Date()}
                                onAny={clear}
                                onToday={setToday}
                                onApply={() => setOpen(false)}
                            />
                        </div>
                    </ClickAwayListener>,
                    document.body,
                )}
        </>
    );
};

const RangeSlider = ({ value, onChange, min, max, step, formatValue }) => (
    <div className="bg-cm-p-surface-container border border-cm-p-outline rounded-[6px] px-4 py-3 flex flex-col gap-1">
        <Slider value={value} min={min} max={max} step={step} onChange={(_, v) => onChange(v)} />
        <div className="text-center text-[14px] font-medium text-cm-p-text-primary">
            {formatValue(value[0])} <span className="text-cm-p-text-secondary">—</span> {formatValue(value[1])}
        </div>
    </div>
);

const formatNumber = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);

// Stable composite key for a broker. Brokers are unique per (name, provider) in the DB;
// the same DOT/MC number can appear under several name spellings, so it can't be the key.
const brokerKey = (broker, index = 0) => {
    const parts = [broker.provider ?? "", broker.name ?? broker.dotNumber ?? broker.mcNumber ?? `idx${index}`];
    return parts.join("-");
};

const initialFromTruckAndPrefill = (truck, prefill) => {
    const today = dayjs().format("YYYY-MM-DD");
    const date = truck?.deliveryDate ? dayjs(truck.deliveryDate).format("YYYY-MM-DD") : today;
    const pickup = truck?.location ? { zip: truck.location.zip, placeName: truck.location.city, stateCode: truck.location.state } : null;
    const pickupRadius =
        prefill?.pickupRadius != null ? (radiusTypes.find((r) => r.id === String(prefill.pickupRadius)) ?? radiusTypes[2]) : radiusTypes[2];
    const deliveryRadius =
        prefill?.deliveryRadius != null
            ? (radiusTypes.find((r) => r.id === String(prefill.deliveryRadius)) ?? radiusTypes[2])
            : radiusTypes[2];
    return {
        pickupLocation: pickup,
        pickupLocationQuery: pickup ? `${pickup.placeName}, ${pickup.stateCode}` : "",
        pickupRadius,
        pickupDateStart: date,
        pickupDateEnd: date,
        deliveryDateStart: null,
        deliveryDateEnd: null,
        deliveryLocation: null,
        deliveryLocationQuery: "",
        deliveryRadius,
        outboundMin: prefill?.outboundDistanceMin ?? OUTBOUND_MIN,
        outboundMax: prefill?.outboundDistanceMax ?? OUTBOUND_MAX,
        minOffer: prefill?.minOffer != null ? String(prefill.minOffer) : "",
        minRpm: prefill?.minRpm != null ? String(prefill.minRpm) : "",
        earnMoreOn: 2,
        brokers: [],
        pickupMarkets: [],
        deliveryMarkets: [],
        stops: stopsOptions[0],
        weightMin: WEIGHT_MIN,
        weightMax: WEIGHT_MAX,
    };
};

const initialFromFilter = (filter) => {
    const pickup = filter.departure
        ? { zip: filter.departure.zip, placeName: filter.departure.city, stateCode: filter.departure.state }
        : null;
    const drop = filter.dropOff ? { zip: filter.dropOff.zip, placeName: filter.dropOff.city, stateCode: filter.dropOff.state } : null;
    return {
        pickupLocation: pickup,
        pickupLocationQuery: pickup ? `${pickup.placeName}, ${pickup.stateCode}` : "",
        pickupRadius: radiusTypes.find((r) => r.id === String(filter.pickupRadius)) ?? radiusTypes[2],
        pickupDateStart: filter.availableDateStart ? filter.availableDateStart.slice(0, 10) : dayjs().format("YYYY-MM-DD"),
        pickupDateEnd: filter.availableDateEnd ? filter.availableDateEnd.slice(0, 10) : dayjs().format("YYYY-MM-DD"),
        deliveryDateStart: filter.deliveryDateStart ?? null,
        deliveryDateEnd: filter.deliveryDateEnd ?? null,
        deliveryLocation: drop,
        deliveryLocationQuery: drop ? `${drop.placeName}, ${drop.stateCode}` : "",
        deliveryRadius: radiusTypes.find((r) => r.id === String(filter.deliveryRadius)) ?? radiusTypes[2],
        outboundMin: filter.outboundDistanceMin ?? OUTBOUND_MIN,
        outboundMax: filter.outboundDistanceMax ?? OUTBOUND_MAX,
        minOffer: filter.minOffer != null ? String(filter.minOffer) : "",
        minRpm: filter.minRpm != null ? String(filter.minRpm) : "",
        earnMoreOn: filter.earnMoreOn ?? 2,
        brokers: (filter.brokers ?? []).map((b, i) => brokerKey(b, i)),
        pickupMarkets: filter.pickupMarkets ?? [],
        deliveryMarkets: filter.deliveryMarkets ?? [],
        stops: stopsOptions.find((s) => s.id === filter.stopsFilter) ?? stopsOptions[0],
        weightMin: filter.minWeight ?? WEIGHT_MIN,
        weightMax: filter.maxWeight ?? WEIGHT_MAX,
    };
};

export const CreateFilterModal = ({
    isOpen,
    mode = "create",
    selectedTruck,
    selectedFilter,
    prefill,
    onClose,
    onSubmit,
    loading,
    error,
}) => {
    const isEdit = mode === "edit";

    const [form, setForm] = useState(() => initialFromTruckAndPrefill(selectedTruck, prefill));
    const update = (patch) => setForm((s) => ({ ...s, ...patch }));
    // A delivery window that ends before pickup can start matches nothing, so moving pickup past it clears it.
    const setPickupDateStart = (d) =>
        setForm((s) => ({
            ...s,
            pickupDateStart: d,
            ...(s.deliveryDateEnd && s.deliveryDateEnd < d ? { deliveryDateStart: null, deliveryDateEnd: null } : {}),
        }));

    const [advancedOpen, setAdvancedOpen] = useState(false);

    // Brokers list (loaded once when modal opens). Each broker shares a `provider` with
    // others (the load board), so we build a composite stable key for React + lookup.
    const [brokerOptions, setBrokerOptions] = useState([]);
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await API.get("/parade/brokers");
                if (!cancelled) {
                    const opts = (data ?? []).map((b, i) => ({
                        id: brokerKey(b, i),
                        text: b.name || b.dotNumber || b.mcNumber || b.provider,
                        broker: b,
                    }));
                    setBrokerOptions(opts);
                }
            } catch (e) {
                console.warn("Failed to load brokers", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    // Market areas (KMAs) for pickup/delivery markets dropdowns.
    const [marketOptions, setMarketOptions] = useState([]);
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await API.get("/marketinfo/markets");
                if (!cancelled) {
                    const opts = (data ?? []).map((m) => ({
                        id: m.kma,
                        text: m.fullName || `${m.city}, ${m.state}`,
                    }));
                    setMarketOptions(opts);
                }
            } catch (e) {
                console.warn("Failed to load markets", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    // Re-seed form whenever the modal opens (or mode/filter/truck changes).
    useEffect(() => {
        if (!isOpen) return;
        if (isEdit && selectedFilter) setForm(initialFromFilter(selectedFilter));
        else setForm(initialFromTruckAndPrefill(selectedTruck, prefill));
        setAdvancedOpen(false);
    }, [isOpen, isEdit, selectedFilter, selectedTruck, prefill]);

    // Default brokers/markets to "all selected" once their options arrive — but only if
    // the form hasn't already been populated (e.g. edit mode hydrated a subset). Tracked
    // per-modal-open via a ref so user-driven deselect-all isn't clobbered later.
    const initRef = useRef({ brokers: false, pickupMarkets: false, deliveryMarkets: false });
    useEffect(() => {
        if (!isOpen) {
            initRef.current = { brokers: false, pickupMarkets: false, deliveryMarkets: false };
        }
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen || brokerOptions.length === 0 || initRef.current.brokers) return;
        initRef.current.brokers = true;
        setForm((s) => (s.brokers && s.brokers.length > 0 ? s : { ...s, brokers: brokerOptions.map((o) => o.id) }));
    }, [isOpen, brokerOptions]);
    useEffect(() => {
        if (!isOpen || marketOptions.length === 0) return;
        const allIds = marketOptions.map((o) => o.id);
        if (!initRef.current.pickupMarkets) {
            initRef.current.pickupMarkets = true;
            setForm((s) => (s.pickupMarkets && s.pickupMarkets.length > 0 ? s : { ...s, pickupMarkets: allIds }));
        }
        if (!initRef.current.deliveryMarkets) {
            initRef.current.deliveryMarkets = true;
            setForm((s) => (s.deliveryMarkets && s.deliveryMarkets.length > 0 ? s : { ...s, deliveryMarkets: allIds }));
        }
    }, [isOpen, marketOptions]);

    // Live "matching loads" counter:
    //   1) Server-side fetch only depends on backend filter params (pickup/delivery/dates/radii/trailer).
    //      Refetch is debounced and only triggers when those params actually change.
    //   2) All other criteria (weight, outbound, offer, RPM, brokers, markets, stops) are applied
    //      client-side over the cached fetch result, so the count reacts immediately to slider/toggle
    //      changes without re-hitting the API.
    const debouncedForm = useDebounce(form, COUNT_DEBOUNCE_MS);
    const [fetchedLoads, setFetchedLoads] = useState(null);
    useEffect(() => {
        if (!isOpen || !debouncedForm.pickupLocation?.zip) {
            setFetchedLoads(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const result = await fetchRequirementsLoads({
                    trailerType: selectedTruck?.trailerType,
                    pickup: debouncedForm.pickupLocation,
                    delivery: debouncedForm.deliveryLocation,
                    pickupDateStart: debouncedForm.pickupDateStart,
                    pickupDateEnd: debouncedForm.pickupDateEnd,
                    deliveryDateStart: debouncedForm.deliveryDateStart,
                    deliveryDateEnd: debouncedForm.deliveryDateEnd,
                    pickupRadius: parseInt(debouncedForm.pickupRadius?.id ?? "150", 10),
                    deliveryRadius: debouncedForm.deliveryLocation ? parseInt(debouncedForm.deliveryRadius?.id ?? "150", 10) : null,
                });
                if (!cancelled) setFetchedLoads(Array.isArray(result) ? result : null);
            } catch {
                if (!cancelled) setFetchedLoads(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        isOpen,
        debouncedForm.pickupLocation,
        debouncedForm.deliveryLocation,
        debouncedForm.pickupDateStart,
        debouncedForm.pickupDateEnd,
        debouncedForm.deliveryDateStart,
        debouncedForm.deliveryDateEnd,
        debouncedForm.pickupRadius,
        debouncedForm.deliveryRadius,
        selectedTruck?.trailerType,
    ]);

    const matchingLoadsCount = useMemo(() => {
        if (!fetchedLoads) return null;
        const minOffer = form.minOffer && !isNaN(Number(form.minOffer)) ? Number(form.minOffer) : null;
        const minRpm = form.minRpm && !isNaN(Number(form.minRpm)) ? Number(form.minRpm) : null;
        const isWeightDefault = form.weightMin === WEIGHT_MIN && form.weightMax === WEIGHT_MAX;
        const isOutboundDefault = form.outboundMin === OUTBOUND_MIN && form.outboundMax === OUTBOUND_MAX;
        const brokersFilterActive = form.brokers.length > 0 && form.brokers.length < brokerOptions.length;
        const selectedBrokerNames = brokersFilterActive
            ? new Set(form.brokers.map((id) => brokerOptions.find((b) => b.id === id)?.broker?.name).filter(Boolean))
            : null;
        const pickupMarketsActive = form.pickupMarkets.length > 0 && form.pickupMarkets.length < marketOptions.length;
        const deliveryMarketsActive = form.deliveryMarkets.length > 0 && form.deliveryMarkets.length < marketOptions.length;
        const pickupMarketSet = pickupMarketsActive ? new Set(form.pickupMarkets) : null;
        const deliveryMarketSet = deliveryMarketsActive ? new Set(form.deliveryMarkets) : null;
        const stopsFilter = form.stops?.id ?? "any";

        return fetchedLoads.filter((load) => {
            if (!isWeightDefault) {
                const w = load.loadWeight ?? 0;
                if (w < form.weightMin || w > form.weightMax) return false;
            }
            // Outbound distance only applies when no specific delivery zip is set.
            if (!form.deliveryLocation && !isOutboundDefault) {
                if (load.loadDistance != null && (load.loadDistance < form.outboundMin || load.loadDistance > form.outboundMax)) {
                    return false;
                }
            }
            if (minOffer != null) {
                if (load.offerRate == null || load.offerRate < minOffer) return false;
            }
            if (minRpm != null) {
                const rpm = load.rpm ?? (load.loadDistance && load.offerRate ? load.offerRate / load.loadDistance : null);
                if (rpm == null || rpm < minRpm) return false;
            }
            if (selectedBrokerNames) {
                const name = load.brokerInfo?.name;
                if (!name || !selectedBrokerNames.has(name)) return false;
            }
            if (pickupMarketSet) {
                const firstStop = load.itineraryStops?.[0];
                if (!firstStop?.marketName || !pickupMarketSet.has(firstStop.marketName)) return false;
            }
            if (deliveryMarketSet) {
                const lastStop = load.itineraryStops?.[load.itineraryStops.length - 1];
                if (!lastStop?.marketName || !deliveryMarketSet.has(lastStop.marketName)) return false;
            }
            if (stopsFilter !== "any") {
                const stopCount = Math.max(0, (load.itineraryStops?.length ?? 0) - 2);
                if (stopsFilter === "single" && stopCount !== 0) return false;
                if (stopsFilter === "multi" && stopCount === 0) return false;
            }
            return true;
        }).length;
    }, [fetchedLoads, form, brokerOptions, marketOptions]);

    const canSubmit = !!form.pickupLocation && !loading;

    const handleSubmit = () => {
        if (!canSubmit) return;
        const isWeightDefault = form.weightMin === WEIGHT_MIN && form.weightMax === WEIGHT_MAX;
        const payload = {
            truckName: selectedTruck?.truckName,
            departureZip: form.pickupLocation.zip,
            trailerType: selectedTruck?.trailerType,
            pickupRadius: parseInt(form.pickupRadius?.id ?? "150", 10),
            availableDateStart: `${form.pickupDateStart}T00:00:00+00:00`,
            availableDateEnd: `${form.pickupDateEnd}T23:59:59+00:00`,
            ...(form.deliveryDateStart ? { deliveryDateStart: form.deliveryDateStart, deliveryDateEnd: form.deliveryDateEnd } : {}),
            ...(form.deliveryLocation
                ? {
                      destination: form.deliveryLocation.zip,
                      deliveryRadius: parseInt(form.deliveryRadius?.id ?? "150", 10),
                  }
                : {}),
            ...(form.deliveryLocation ? {} : { outboundDistanceMin: form.outboundMin, outboundDistanceMax: form.outboundMax }),
            ...(form.minOffer && !isNaN(Number(form.minOffer)) ? { minOffer: Number(form.minOffer) } : {}),
            ...(form.minRpm && !isNaN(Number(form.minRpm)) ? { minRpm: Number(form.minRpm) } : {}),
            ...(form.earnMoreOn != null ? { earnMoreOn: form.earnMoreOn } : {}),
            ...(form.brokers && form.brokers.length > 0 && form.brokers.length < brokerOptions.length
                ? {
                      brokers: form.brokers.map((id) => brokerOptions.find((b) => b.id === id)?.broker).filter(Boolean),
                  }
                : {}),
            ...(form.pickupMarkets && form.pickupMarkets.length > 0 && form.pickupMarkets.length < marketOptions.length
                ? { pickupMarkets: form.pickupMarkets }
                : {}),
            ...(form.deliveryMarkets && form.deliveryMarkets.length > 0 && form.deliveryMarkets.length < marketOptions.length
                ? { deliveryMarkets: form.deliveryMarkets }
                : {}),
            ...(form.stops && form.stops.id !== "any" ? { stopsFilter: form.stops.id } : {}),
            ...(isWeightDefault ? {} : { minWeight: form.weightMin, maxWeight: form.weightMax }),
        };
        onSubmit?.(payload, { isEdit });
    };

    const radiusOpts = useMemo(() => radiusTypes.map((r) => ({ id: r.id, text: r.text })), []);
    const deliveryMinDate = dayjs(form.pickupDateStart).isAfter(dayjs(), "day") ? dayjs(form.pickupDateStart).toDate() : undefined;

    const subtitle = isEdit
        ? "Update the criteria below. Changes apply on save."
        : "Let brokers know your truck is available for loads that match your criteria. Once the filter is created, you'll also start seeing matching loads.";

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay fixed inset-0 bg-black/70 z-modal flex items-center justify-center"
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
        >
            <div className="bg-cm-p-surface border border-cm-p-outline rounded-[12px] w-[1000px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] flex flex-col pointer-events-auto shadow-xl overflow-clip">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-cm-p-outline px-6 py-4 gap-4">
                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[18px] font-medium text-cm-p-text-primary tracking-[-0.18px]">
                            <TranslatedText>{isEdit ? "Edit filter" : "Create a filter"}</TranslatedText>
                        </span>
                        <span className="text-[13px] text-cm-p-text-secondary">
                            <TranslatedText>{subtitle}</TranslatedText>
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex items-center justify-center w-10 h-10 rounded hover:bg-cm-p-surface-hover text-cm-p-text-primary shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                    {/* Row 1: pickup + delivery + brokers */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <FieldLabel>Pickup</FieldLabel>
                            <div className="flex gap-2">
                                <div className="flex-1 min-w-0">
                                    <LocationDropdown
                                        name="modalPickup"
                                        value={form.pickupLocation}
                                        setValue={(v) => update({ pickupLocation: v })}
                                        query={form.pickupLocationQuery}
                                        setQuery={(q) => update({ pickupLocationQuery: q })}
                                        label=""
                                        theme="truck"
                                    />
                                </div>
                                <div className="w-[110px] shrink-0">
                                    <SelectField
                                        value={form.pickupRadius}
                                        onChange={(v) => update({ pickupRadius: v })}
                                        options={radiusOpts}
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <FieldLabel>Delivery (optional)</FieldLabel>
                            <div className="flex gap-2">
                                <div className="flex-1 min-w-0">
                                    <LocationDropdown
                                        name="modalDelivery"
                                        value={form.deliveryLocation}
                                        setValue={(v) => update({ deliveryLocation: v })}
                                        query={form.deliveryLocationQuery}
                                        setQuery={(q) => update({ deliveryLocationQuery: q })}
                                        label=""
                                        theme="truck"
                                    />
                                </div>
                                <div className="w-[110px] shrink-0">
                                    <SelectField
                                        value={form.deliveryRadius}
                                        onChange={(v) => update({ deliveryRadius: v })}
                                        options={radiusOpts}
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <FieldLabel>Broker</FieldLabel>
                            <MultiSelectField
                                value={form.brokers}
                                onChange={(v) => update({ brokers: v })}
                                options={brokerOptions}
                                allLabel="brokers"
                            />
                        </div>
                    </div>

                    {/* Row 2: pickup dates + delivery dates + min offer/min rpm */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <FieldLabel>Pickup dates</FieldLabel>
                            <DateRangeField
                                startDate={form.pickupDateStart}
                                endDate={form.pickupDateEnd}
                                onChangeStart={setPickupDateStart}
                                onChangeEnd={(d) => update({ pickupDateEnd: d })}
                            />
                        </div>
                        <div>
                            <FieldLabel>Delivery dates (optional)</FieldLabel>
                            <DateRangeField
                                startDate={form.deliveryDateStart}
                                endDate={form.deliveryDateEnd}
                                onChangeStart={(d) => update({ deliveryDateStart: d })}
                                onChangeEnd={(d) => update({ deliveryDateEnd: d })}
                                onClear={() => update({ deliveryDateStart: null, deliveryDateEnd: null })}
                                minDate={deliveryMinDate}
                                placeholder="Any"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <FieldLabel>Min offer</FieldLabel>
                                <TextField
                                    value={form.minOffer}
                                    onChange={(v) => update({ minOffer: v })}
                                    numeric
                                    placeholder="Any"
                                    prefix="$"
                                />
                            </div>
                            <div>
                                <FieldLabel>Min RPM</FieldLabel>
                                <TextField
                                    value={form.minRpm}
                                    onChange={(v) => update({ minRpm: v })}
                                    numeric
                                    placeholder="Any"
                                    prefix="$"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <button
                            type="button"
                            onClick={() => setAdvancedOpen((v) => !v)}
                            aria-expanded={advancedOpen}
                            className="flex items-center gap-1 text-[12px] font-medium uppercase tracking-wider text-cm-p-text-secondary self-start hover:text-cm-p-text-primary transition-colors"
                        >
                            <TranslatedText>Advanced parameters</TranslatedText>
                            <ChevronDown
                                className={`w-4 h-4 transition-transform duration-400 ease-smooth motion-reduce:transition-none ${
                                    advancedOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        <div
                            className={`grid transition-[grid-template-rows] duration-400 ease-smooth motion-reduce:transition-none ${
                                advancedOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                            }`}
                        >
                            <div className="overflow-hidden">
                                {/* `invisible` while collapsed keeps the hidden fields out of the tab order. */}
                                <div
                                    className={`flex flex-col gap-4 pt-4 transition-[opacity,transform,visibility] duration-240 ease-smooth motion-reduce:transition-none ${
                                        advancedOpen
                                            ? "visible opacity-100 translate-y-0 delay-40"
                                            : "invisible opacity-0 -translate-y-1.5"
                                    }`}
                                >
                                    {/* Row 3: pickup markets + delivery markets + stops */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <FieldLabel>Pickup markets</FieldLabel>
                                            <MultiSelectField
                                                value={form.pickupMarkets}
                                                onChange={(v) => update({ pickupMarkets: v })}
                                                options={marketOptions}
                                                allLabel="markets"
                                            />
                                        </div>
                                        <div>
                                            <FieldLabel>Delivery markets</FieldLabel>
                                            <MultiSelectField
                                                value={form.deliveryMarkets}
                                                onChange={(v) => update({ deliveryMarkets: v })}
                                                options={marketOptions}
                                                allLabel="markets"
                                            />
                                        </div>
                                        <div>
                                            <FieldLabel>Stops</FieldLabel>
                                            <SelectField value={form.stops} onChange={(v) => update({ stops: v })} options={stopsOptions} />
                                        </div>
                                    </div>

                                    {/* Row 4: weight + earn more on + outbound distance */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <FieldLabel>Weight (LBS)</FieldLabel>
                                            <RangeSlider
                                                value={[form.weightMin, form.weightMax]}
                                                onChange={(v) => update({ weightMin: v[0], weightMax: v[1] })}
                                                min={WEIGHT_MIN}
                                                max={WEIGHT_MAX}
                                                step={WEIGHT_STEP}
                                                formatValue={(n) => `${formatNumber(n)}`}
                                            />
                                        </div>
                                        <div>
                                            <FieldLabel>Earn more on</FieldLabel>
                                            <div className="bg-cm-p-surface-container border border-cm-p-outline rounded-[6px] px-4 py-3 flex flex-col gap-1">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[13px] font-medium text-cm-p-text-primary w-7">Line</span>
                                                    <div className="flex-1 flex items-center">
                                                        <Slider
                                                            value={form.earnMoreOn}
                                                            step={null}
                                                            min={0}
                                                            max={4}
                                                            marks={lineExitMarks}
                                                            onChange={(_, v) => update({ earnMoreOn: v })}
                                                        />
                                                    </div>
                                                    <span className="text-[13px] font-medium text-cm-p-text-primary w-7 text-right">
                                                        Exit
                                                    </span>
                                                </div>
                                                <div className="text-center text-[14px] font-medium text-cm-p-text-primary">
                                                    {earnMoreOnLabels[form.earnMoreOn]}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <FieldLabel>Outbound distance</FieldLabel>
                                            <RangeSlider
                                                value={[form.outboundMin, form.outboundMax]}
                                                onChange={(v) => update({ outboundMin: v[0], outboundMax: v[1] })}
                                                min={OUTBOUND_MIN}
                                                max={OUTBOUND_MAX}
                                                step={OUTBOUND_STEP}
                                                formatValue={(n) => `${formatNumber(n)} mi`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && <div className="text-[13px] text-cm-p-on-container-red">{error}</div>}
                </div>

                {/* Footer */}
                <div className="border-t border-cm-p-outline px-6 py-4 flex items-center justify-between gap-4">
                    <span className="text-[13px] text-cm-p-text-secondary">
                        {matchingLoadsCount != null && (
                            <TranslatedText>
                                {matchingLoadsCount === 0
                                    ? "There are no loads matching these requirements"
                                    : `There are ${matchingLoadsCount} loads matching these requirements`}
                            </TranslatedText>
                        )}
                    </span>
                    <div className="flex items-center gap-[10px]">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-[120px] h-[44px] flex items-center justify-center rounded-[6px] bg-cm-p-surface-container text-cm-p-text-primary text-[14px] font-medium border border-cm-p-outline"
                        >
                            <TranslatedText>Cancel</TranslatedText>
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className={`w-[120px] h-[44px] flex items-center justify-center rounded-[6px] text-[14px] font-medium ${
                                canSubmit
                                    ? "bg-cm-p-accent-500 text-cm-p-neutral-dark-500"
                                    : "bg-cm-p-accent-50 text-cm-p-text-secondary cursor-not-allowed"
                            }`}
                        >
                            <TranslatedText>{isEdit ? "Save" : "Create"}</TranslatedText>
                        </button>
                    </div>
                </div>
            </div>
        </ReactModal>
    );
};
